import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save, Cancel, ArrowBack, LockReset } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import { useFormValidation } from '@/hooks/useFormValidation';
import { required, phone, email, timeHHMM } from '@/utils/validators';

const AddTeacher = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subjects: '',
    start_time: '09:00',
    end_time: '15:00',
    alternative_phone: '',
    address: ''
  });

  const schema = {
    name: [required('Full name')],
    phone: [required('Phone number'), phone],
    alternative_phone: [phone],
    email: [required('Email'), email],
    start_time: [required('Start time'), timeHHMM],
    end_time: [required('End time'), timeHHMM],
  };
  const { handleBlur, validateAll, applyServerErrors, showError } = useFormValidation(schema);

  // Fetch teacher data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchTeacher = async () => {
        try {
          setLoading(true);
          const response = await principalAPI.getTeacher(id);
          const teacher = response.data.teacher;
          setFormData({
            name: teacher.user?.name || '',
            email: teacher.user?.email || '',
            phone: teacher.user?.phone || '',
            subjects: Array.isArray(teacher.subjects) ? teacher.subjects.join(', ') : (teacher.subjects || ''),
            start_time: teacher.start_time || '09:00',
            end_time: teacher.end_time || '15:00',
            alternative_phone: teacher.alternative_phone || '',
            address: teacher.address || ''
          });
        } catch (error) {
          console.error('Error fetching teacher:', error);
          setError('Failed to load teacher data');
        } finally {
          setLoading(false);
        }
      };
      fetchTeacher();
    }
  }, [isEditMode, id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll(formData)) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Convert subjects from comma-separated string to array
      const teacherData = {
        ...formData,
        subjects: formData.subjects ? formData.subjects.split(',').map(s => s.trim()) : []
      };

      if (isEditMode) {
        await principalAPI.updateTeacher(id, teacherData);
        setSuccess('Teacher updated successfully!');
      } else {
        await principalAPI.createTeacher(teacherData);
        setSuccess('Teacher created successfully! A password setup link has been emailed to them.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subjects: '',
          start_time: '09:00',
          end_time: '15:00',
          alternative_phone: '',
          address: ''
        });
      }

      // Navigate back to teachers list after 2 seconds
      setTimeout(() => {
        navigate('/principal/teachers');
      }, 2000);

    } catch (error) {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        applyServerErrors(serverErrors);
      } else {
        setError(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} teacher`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    setError('');
    try {
      await principalAPI.resetTeacherPassword(id);
      setSuccess('Password reset link sent to the teacher\'s email!');
      setResetDialogOpen(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {isEditMode ? 'Edit Teacher' : 'Add New Teacher'}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => handleBlur('name', e.target.value)}
                error={Boolean(showError('name'))}
                helperText={showError('name')}
                placeholder="Enter teacher's full name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('phone', e.target.value)}
                error={Boolean(showError('phone'))}
                helperText={showError('phone')}
                placeholder="+1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alternative Phone Number"
                name="alternative_phone"
                value={formData.alternative_phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('alternative_phone', e.target.value)}
                error={Boolean(showError('alternative_phone'))}
                helperText={showError('alternative_phone')}
                placeholder="+1234567890"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter teacher's address"
                multiline
                rows={2}
              />
            </Grid>

            {/* Set Login */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Set Login
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Set Email ID"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={(e) => handleBlur('email', e.target.value)}
                error={Boolean(showError('email'))}
                placeholder="teacher@example.com"
                helperText={showError('email') || (isEditMode ? "Changing email will update email ID" : "This will be used as email ID")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {isEditMode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<LockReset />}
                    onClick={() => setResetDialogOpen(true)}
                    sx={{ py: 1.5 }}
                  >
                    Reset Password
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    A password setup link will be emailed to the teacher.
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Additional Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subjects"
                name="subjects"
                value={formData.subjects}
                onChange={handleChange}
                placeholder="Math, Science, English (comma-separated)"
                helperText="Enter subjects separated by commas"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Start Time"
                name="start_time"
                type="time"
                value={formData.start_time}
                onChange={handleChange}
                onBlur={(e) => handleBlur('start_time', e.target.value)}
                error={Boolean(showError('start_time'))}
                helperText={showError('start_time')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="End Time"
                name="end_time"
                type="time"
                value={formData.end_time}
                onChange={handleChange}
                onBlur={(e) => handleBlur('end_time', e.target.value)}
                error={Boolean(showError('end_time'))}
                helperText={showError('end_time')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/principal/teachers')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Teacher' : 'Create Teacher')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Teacher Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            A password reset link will be emailed to <strong>{formData.name}</strong> ({formData.email}). The link expires in 1 hour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={resetLoading}
            startIcon={resetLoading ? <CircularProgress size={20} /> : <LockReset />}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddTeacher;
